// Status vocabulary for Pulsars, check-ins and proposals — label AND a distinct
// icon shape per state, semantic tokens only, never colour alone
// (standing-agent-ui "Status vocabulary and rendering"). Kept apart from the
// badge components so those files export only components (react-refresh).

import {
  arrowRotateCwIcon,
  checkCircleIcon,
  checkIcon,
  clockIcon,
  exclamationCircleIcon,
  handIcon,
  pauseIcon,
  pencilIcon,
  playIcon,
  calendarIcon,
  xCircleIcon,
  minusCircleIcon,
  starIcon,
  stopIcon,
  questionCircleIcon,
  type SVGIcon,
} from '@progress/kendo-svg-icons';
import type { CheckInEntryStatus, CheckInTriggerLabel, ProposalStatus, StandingStatus } from '@shared/api-types';

export interface BadgeMeta {
  label: string;
  icon: SVGIcon;
  /** A semantic token (var(--…)). */
  color: string;
}

/** Pulsar states: Draft, Active, Checking in, Waiting for you, Paused, Halted, Done. */
export const STANDING_STATUS: Record<StandingStatus, BadgeMeta> = {
  draft: { label: 'Draft', icon: pencilIcon, color: 'var(--fg-muted)' },
  active: { label: 'Active', icon: playIcon, color: 'var(--success)' },
  checkingIn: { label: 'Checking in', icon: arrowRotateCwIcon, color: 'var(--accent)' },
  waitingForYou: { label: 'Waiting for you', icon: exclamationCircleIcon, color: 'var(--trigger)' },
  paused: { label: 'Paused', icon: pauseIcon, color: 'var(--fg-muted)' },
  halted: { label: 'Halted', icon: handIcon, color: 'var(--danger)' },
  done: { label: 'Done', icon: checkCircleIcon, color: 'var(--success)' },
};

/** Proposal states: Pending, Approved, Auto-approved, Denied, Expired, Running, Done, Failed. */
export const PROPOSAL_STATUS: Record<ProposalStatus, BadgeMeta> = {
  pending: { label: 'Pending', icon: clockIcon, color: 'var(--trigger)' },
  approved: { label: 'Approved', icon: checkIcon, color: 'var(--accent)' },
  autoApproved: { label: 'Auto-approved', icon: starIcon, color: 'var(--accent)' },
  denied: { label: 'Denied', icon: minusCircleIcon, color: 'var(--fg-muted)' },
  expired: { label: 'Expired', icon: calendarIcon, color: 'var(--fg-muted)' },
  running: { label: 'Running', icon: arrowRotateCwIcon, color: 'var(--accent)' },
  done: { label: 'Done', icon: checkCircleIcon, color: 'var(--success)' },
  failed: { label: 'Failed', icon: xCircleIcon, color: 'var(--danger)' },
};

/** Check-in outcomes (activity timeline + the last-10 strip). */
export const CHECK_IN_STATUS: Record<CheckInEntryStatus, BadgeMeta> = {
  running: { label: 'Checking in', icon: arrowRotateCwIcon, color: 'var(--accent)' },
  nothingToDo: { label: 'Nothing to do', icon: minusCircleIcon, color: 'var(--fg-muted)' },
  done: { label: 'Done', icon: checkCircleIcon, color: 'var(--success)' },
  stopped: { label: 'Stopped', icon: stopIcon, color: 'var(--fg-muted)' },
  failed: { label: 'Failed', icon: xCircleIcon, color: 'var(--danger)' },
  timedOut: { label: 'Timed out', icon: clockIcon, color: 'var(--danger)' },
  unknown: { label: 'Unknown', icon: questionCircleIcon, color: 'var(--fg-muted)' },
};

/** What started a check-in. */
export const CHECK_IN_TRIGGER: Record<CheckInTriggerLabel, string> = {
  schedule: 'Schedule',
  event: 'Event',
  manual: 'Manual',
  trial: 'Trial',
  continuous: 'Keep going',
};

export function checkInStatusMeta(status: CheckInEntryStatus | null | undefined): BadgeMeta {
  return CHECK_IN_STATUS[status ?? 'unknown'] ?? CHECK_IN_STATUS.unknown;
}
